import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { datadogLogs, LogsEvent } from '@datadog/browser-logs';

import { WINDOW } from './injection-tokens';
import { AngularticsConfigService } from './angulartics-config.service';
import moment from 'moment';
import { isPure1Production } from '../utils/url';
import { filter, Subject, takeUntil } from 'rxjs';
import { smartTimer } from '@pstg/smart-timer';
import { environment } from '../environments/environment';
import { ActivatedRoute, Data, NavigationEnd, Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class DatadogLoggingConfigService implements OnDestroy {
    private readonly resetIntervalSeconds = 10;
    private readonly throttleAfterCount = 3;
    private readonly logCounter = new Map<String, { count: number; log: LogsEvent }>();
    private readonly ignoredLogs: (string | RegExp)[] = [
        // Use string for exact message match
        // Amplitude: Ignore errors from Amplitude Logger, which is usually caused by network calls being blocked by an adblocker
        /console error: Amplitude Logger \[Error\]/, // Example: console error: Amplitude Logger [Error]: TypeError: Failed to fetch'
        'Fetch error POST https://api2.amplitude.com/2/httpapi',
        'Fetch error POST https://api2.amplitude.com/batch',
    ];
    private readonly destroy$ = new Subject<void>();

    constructor(
        @Inject(WINDOW) private readonly window: Window,
        private readonly angularticsConfig: AngularticsConfigService,
        private readonly ngZone: NgZone,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
    ) {}

    getCurrentRouteData(): Data {
        let data = {};
        let route: ActivatedRoute = this.route;
        while (route.firstChild) {
            data = { ...data, ...route.snapshot.data };
            route = route.firstChild;
        }
        return data;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
    }

    run(): void {
        this.ngZone.runOutsideAngular(() =>
            smartTimer(
                moment.duration(this.resetIntervalSeconds, 'seconds').asMilliseconds(),
                moment.duration(this.resetIntervalSeconds, 'seconds').asMilliseconds(),
                moment.duration(this.resetIntervalSeconds, 'seconds').asMilliseconds(),
            )
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => this.logCounter.clear()),
        );
        datadogLogs.init({
            clientToken: 'pub33808b4d77f7b77dbad23f13e95305e1',
            forwardErrorsToLogs: true,
            // DEPRECATED: this service name doesn't match RUM's 'pure1-gui' and contains a
            // double-dot typo. Sourcemaps used to be uploaded a second time under this name too,
            // so stack traces in these logs unminified; that upload was removed on 2026-08-16, so
            // **stack traces logged under this service are now minified**. The monitors below
            // still count these log events correctly — the five subscription ones match on view
            // path and message text, and 173201287 on host plus a full-text "draas-api"; none of
            // them reads a symbol name — but anyone opening one to debug gets no readable frames.
            //
            // Switch this to 'pure1-gui' once these Datadog monitors are migrated. All six were
            // confirmed live and still filtering on service:pure1..com on 2026-08-16:
            //   146368886 Appliance Genealogy UI error rate      (dx-submerce-alert-prd)
            //   146358732 Evergreen forever&foundation UI        (dx-submerce-alert-prd)
            //   146360182 Asset Management UI error rate         (dx-submerce-alert-prd)
            //   146371461 Orders Page UI error rate              (dx-submerce-alert-prd)
            //   145345957 Evergreen one frontend error rate      (dx-submerce-alert-prd)
            //   173201287 DRaaS: P1M GUI log errors              (draas-mercury-alerts)
            service: 'pure1..com',
            sampleRate: 100, // Log 100% of errors
            version: environment.buildVersion,
            env: isPure1Production(this.window.location) ? 'prod' : 'not-prod', // Later, may actually want to split this out by prod/pre-prod/staging/dev/
            beforeSend: (log: LogsEvent) => {
                // discard logs for aborted requests
                if (log.http?.status_code === 0) {
                    return false;
                }
                const count = this.updateLogCount(log);
                return count <= this.throttleAfterCount && !this.isIgnored(log.message);
            },
        });

        this.router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                const pageOwner = this.getCurrentRouteData().ownedBy as string;
                if (pageOwner) {
                    datadogLogs.addLoggerGlobalContext('pageOwner', pageOwner);
                } else {
                    datadogLogs.removeLoggerGlobalContext('pageOwner');
                }
            });

        // Include the Angulartics custom dimensions (once they're available) to provide additional context
        this.angularticsConfig.userIdentified$.subscribe(userIdentityInfo => {
            datadogLogs.addLoggerGlobalContext('original-email-domain', userIdentityInfo.originalEmailDomain);
            datadogLogs.addLoggerGlobalContext('impersonated-email-domain', userIdentityInfo.impersonatedEmailDomain);
            datadogLogs.addLoggerGlobalContext('original-contact-id', userIdentityInfo.originalContactId);
            datadogLogs.addLoggerGlobalContext('impersonated-contact-id', userIdentityInfo.impersonatedContactId);
        });
    }

    updateLogCount(log: LogsEvent): number {
        const key = this.makeKey(log);
        if (!this.logCounter.has(key)) {
            this.logCounter.set(key, { count: 0, log });
        }
        const old = this.logCounter.get(key);
        old.count++;
        return this.logCounter.get(key).count;
    }

    makeKey(log: LogsEvent): string {
        return log.message + '\n' + log.error?.stack?.slice(0, 500);
    }

    isIgnored(message: string): boolean {
        return this.ignoredLogs.some(rule => {
            if (typeof rule === 'string') {
                return message === rule;
            } else {
                return rule.test(message);
            }
        });
    }
}
